import {useMemo, useState} from 'react';
import {FileText, Loader2, Pencil, Trash2, X} from 'lucide-react';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Textarea} from '@common-web/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {cn} from '@common-web/lib/utils';
import {createMutationErrorHandler, trpc, type RouterOutput} from '@common-web/lib/trpc';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {toast} from '@common-web/hooks/use-toast';

/**
 * Notes attached to a member, scoped All / Shared / CBS internal.
 *
 * Two stored scopes back the three filters: SHARED (anyone with eligibility
 * access to the member's group) and CBS_INTERNAL (CBS staff only); "All" is a
 * filter, not a value. Only internal users can author CBS-internal notes, and
 * the server enforces both that rule and the read-time filter — the UI here
 * only mirrors it.
 *
 * VBA's own single free-text `Members.Notes` column is passed in as
 * `legacyNotes` and rendered read-only alongside the real timeline; it predates
 * this model and has no author or scope of its own.
 */

type MemberNote = RouterOutput['vba']['members']['listNotes'][number];

type NoteScope = 'SHARED' | 'CBS_INTERNAL';
type NoteFilter = 'all' | 'SHARED' | 'CBS_INTERNAL';

export type LegacyMemberNote = {
  id: string;
  text: string;
  author?: string | null;
};

const FILTERS: {value: NoteFilter; label: string}[] = [
  {value: 'all', label: 'All'},
  {value: 'SHARED', label: 'Shared'},
  {value: 'CBS_INTERNAL', label: 'CBS internal'},
];

const SCOPE_LABELS: Record<NoteScope, string> = {
  SHARED: 'Shared',
  CBS_INTERNAL: 'CBS internal',
};

function ScopePill({scope}: {scope: NoteScope}) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 rounded px-1.5 py-px text-xs font-semibold uppercase tracking-wide',
        scope === 'CBS_INTERNAL' ? 'bg-amber-50 text-amber-800' : 'bg-sky-50 text-sky-700',
      )}
    >
      {SCOPE_LABELS[scope]}
    </span>
  );
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

type MemberNotesPanelProps = {
  /** Composite VBA member id, `<subscriberId>-<memberSeq>`. */
  memberId: string;
  /** Read-only VBA `Members.Notes` free text, when present. */
  legacyNotes?: LegacyMemberNote[];
};

export function MemberNotesPanel({memberId, legacyNotes = []}: MemberNotesPanelProps) {
  const utils = trpc.useUtils();
  const isInternal = useIsInternal();

  const [filter, setFilter] = useState<NoteFilter>('all');
  const [draft, setDraft] = useState('');
  const [draftScope, setDraftScope] = useState<NoteScope>('SHARED');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState('');
  const [editScope, setEditScope] = useState<NoteScope>('SHARED');

  const notesQuery = trpc.vba.members.listNotes.useQuery({memberId}, {enabled: !!memberId});

  const invalidate = () => {
    void utils.vba.members.listNotes.invalidate({memberId});
  };

  const addNote = trpc.vba.members.addNote.useMutation({
    onSuccess: () => {
      setDraft('');
      setDraftScope('SHARED');
      invalidate();
    },
    onError: createMutationErrorHandler('Error adding note'),
  });

  const updateNote = trpc.vba.members.updateNote.useMutation({
    onSuccess: () => {
      setEditingId(null);
      invalidate();
    },
    onError: createMutationErrorHandler('Error updating note'),
  });

  const deleteNote = trpc.vba.members.deleteNote.useMutation({
    onSuccess: () => {
      toast({title: 'Note deleted'});
      invalidate();
    },
    onError: createMutationErrorHandler('Error deleting note'),
  });

  const notes = notesQuery.data ?? [];

  const visibleNotes = useMemo(() => {
    if (filter === 'all') return notes;
    return notes.filter((n) => n.visibility === filter);
  }, [notes, filter]);

  // Legacy VBA text has no scope of its own; treat it as Shared so it drops out
  // of the CBS-internal filter rather than appearing under every tab.
  const visibleLegacy = filter === 'CBS_INTERNAL' ? [] : legacyNotes;

  const totalCount = notes.length + legacyNotes.length;
  const isEmpty = visibleNotes.length === 0 && visibleLegacy.length === 0;

  const startEditing = (note: MemberNote) => {
    setEditingId(note.id);
    setEditBody(note.body);
    setEditScope(note.visibility as NoteScope);
  };

  return (
    <div
      className={cn(
        'overflow-hidden',
        isInternal ? 'rounded-xl border bg-card' : 'rounded-lg border border-gray-200 bg-white',
      )}
    >
      <div className='flex items-center justify-between gap-3 border-b border-gray-100 px-4 py-2.5'>
        <div className='flex items-center gap-2'>
          <FileText className={cn('h-4 w-4', isInternal ? 'text-primary' : 'text-brand-blue')} />
          <h3
            className={cn(
              'text-[13px] font-semibold',
              isInternal ? 'text-foreground' : 'text-brand-navy',
            )}
          >
            Notes
          </h3>
          {totalCount > 0 && (
            <span
              className={cn(
                'rounded-full px-1.5 py-0.5 text-xs font-semibold',
                isInternal ? 'bg-primary/10 text-primary' : 'bg-brand-blue/10 text-brand-blue',
              )}
            >
              {totalCount}
            </span>
          )}
        </div>
        <div className='flex items-center gap-1'>
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type='button'
              onClick={() => setFilter(f.value)}
              className={cn(
                'rounded-md px-2 py-1 text-xs font-semibold transition-colors',
                filter === f.value
                  ? isInternal
                    ? 'bg-primary/10 text-primary'
                    : 'bg-brand-blue/10 text-brand-blue'
                  : isInternal
                    ? 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                    : 'text-brand-gray hover:bg-gray-50 hover:text-brand-navy',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Composer — internal gets the demo's dense single-line composer (input +
          scope + Add on one row, Enter to save); client keeps the textarea. */}
      {isInternal ? (
        <div className='flex items-center gap-2 border-b border-gray-100 px-4 py-2.5'>
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.trim() && !addNote.isPending) {
                addNote.mutate({memberId, body: draft.trim(), visibility: draftScope});
              }
            }}
            placeholder='Add a note… (Enter to save)'
            className='h-8 flex-1 text-[13px]'
            disabled={addNote.isPending}
          />
          <Select
            value={draftScope}
            onValueChange={(v) => setDraftScope(v as NoteScope)}
            disabled={addNote.isPending}
          >
            <SelectTrigger className='h-8 w-[130px] shrink-0 text-xs'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='SHARED'>Shared</SelectItem>
              <SelectItem value='CBS_INTERNAL'>CBS internal</SelectItem>
            </SelectContent>
          </Select>
          <Button
            type='button'
            size='sm'
            className='shrink-0'
            onClick={() => addNote.mutate({memberId, body: draft.trim(), visibility: draftScope})}
            disabled={!draft.trim() || addNote.isPending}
          >
            {addNote.isPending && <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />}
            Add
          </Button>
        </div>
      ) : (
        <div className='space-y-2 border-b border-gray-100 px-4 py-3'>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder='Add a note…'
            rows={2}
            disabled={addNote.isPending}
          />
          <div className='flex items-center justify-between gap-2'>
            {/* Non-internal users can only post Shared notes; the server rejects
                anything else, so show the fixed scope instead of a dead control. */}
            <ScopePill scope='SHARED' />
            <Button
              type='button'
              size='sm'
              onClick={() => addNote.mutate({memberId, body: draft.trim(), visibility: draftScope})}
              disabled={!draft.trim() || addNote.isPending}
            >
              {addNote.isPending && <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />}
              Add note
            </Button>
          </div>
        </div>
      )}

      {notesQuery.isLoading ? (
        <p className='px-4 py-4 text-center text-[12px] text-brand-gray'>Loading notes…</p>
      ) : isEmpty ? (
        <p className='px-4 py-4 text-center text-[12px] text-brand-gray'>
          {filter === 'all'
            ? 'No notes on file yet.'
            : `No ${filter === 'SHARED' ? 'shared' : 'CBS-internal'} notes.`}
        </p>
      ) : (
        <ul className='max-h-[320px] divide-y divide-gray-100 overflow-y-auto'>
          {visibleNotes.map((note) => (
            <li key={note.id} className='px-4 py-2.5'>
              {editingId === note.id ? (
                <div className='space-y-2'>
                  <Textarea
                    value={editBody}
                    onChange={(e) => setEditBody(e.target.value)}
                    rows={3}
                    disabled={updateNote.isPending}
                  />
                  <div className='flex items-center justify-between gap-2'>
                    {isInternal ? (
                      <Select
                        value={editScope}
                        onValueChange={(v) => setEditScope(v as NoteScope)}
                        disabled={updateNote.isPending}
                      >
                        <SelectTrigger className='h-8 w-[150px] text-xs'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='SHARED'>Shared</SelectItem>
                          <SelectItem value='CBS_INTERNAL'>CBS internal</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <ScopePill scope={editScope} />
                    )}
                    <div className='flex items-center gap-2'>
                      <Button
                        type='button'
                        size='sm'
                        variant='ghost'
                        onClick={() => setEditingId(null)}
                        disabled={updateNote.isPending}
                      >
                        <X className='mr-1 h-3.5 w-3.5' />
                        Cancel
                      </Button>
                      <Button
                        type='button'
                        size='sm'
                        onClick={() =>
                          updateNote.mutate({
                            noteId: note.id,
                            body: editBody.trim(),
                            visibility: editScope,
                          })
                        }
                        disabled={!editBody.trim() || updateNote.isPending}
                      >
                        {updateNote.isPending && (
                          <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className='mb-1 flex items-baseline gap-2'>
                    <span
                      className={cn(
                        'shrink-0 text-[12px] font-semibold',
                        isInternal ? 'text-foreground' : 'text-brand-navy',
                      )}
                    >
                      {note.author}
                    </span>
                    <ScopePill scope={note.visibility as NoteScope} />
                    <span className='text-xs text-brand-gray'>
                      {formatTimestamp(note.createdAt)}
                      {note.updatedAt ? ' · edited' : ''}
                    </span>
                    {note.canEdit && (
                      <span className='ml-auto flex shrink-0 items-center gap-1'>
                        <button
                          type='button'
                          className='rounded-sm p-1 text-brand-gray hover:bg-gray-100 hover:text-brand-navy'
                          onClick={() => startEditing(note)}
                          aria-label='Edit note'
                        >
                          <Pencil className='h-3.5 w-3.5' />
                        </button>
                        <button
                          type='button'
                          className='rounded-sm p-1 text-brand-gray hover:bg-red-50 hover:text-red-600'
                          onClick={() => deleteNote.mutate({noteId: note.id})}
                          disabled={deleteNote.isPending}
                          aria-label='Delete note'
                        >
                          <Trash2 className='h-3.5 w-3.5' />
                        </button>
                      </span>
                    )}
                  </div>
                  <p
                    className={cn(
                      'whitespace-pre-wrap text-[13px]',
                      isInternal ? 'text-foreground' : 'text-brand-navy',
                    )}
                  >
                    {note.body}
                  </p>
                </>
              )}
            </li>
          ))}

          {visibleLegacy.map((note) => (
            <li key={note.id} className='bg-gray-50/50 px-4 py-2.5'>
              <div className='mb-1 flex items-baseline gap-2'>
                {note.author && (
                  <span
                    className={cn(
                      'shrink-0 text-[12px] font-semibold',
                      isInternal ? 'text-foreground' : 'text-brand-navy',
                    )}
                  >
                    {note.author}
                  </span>
                )}
                <ScopePill scope='SHARED' />
                <span className='text-xs text-brand-gray'>Legacy</span>
              </div>
              <p
                className={cn(
                  'whitespace-pre-wrap text-[13px]',
                  isInternal ? 'text-foreground' : 'text-brand-navy',
                )}
              >
                {note.text}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
